/**
 * URL slug ↔ Auditor Business sub-agent id mapping.
 * Used by AuditorPage / AuditorCommandNav / AuditorBusinessPanel.
 *
 * Structure mirrors a real Canadian CCPC's org chart. The 2026-08-17
 * restructure merged Compliance + Corporate Secretary → Corporate Records,
 * and surfaced Receipts & vendors, Assets & CCA, and Investor Readiness as
 * first-class sub-agents.
 */

export const AUDITOR_TABS = [
  { id: 'business', label: 'Business' },
  { id: 'code', label: 'Code' },
  { id: 'security', label: 'Security' },
];

/** slug → backend sub-agent id (null = Command Center / Business Auditor coordinator) */
export const AGENT_SLUG_TO_ID = {
  command: null,
  help: 'help',
  accountant: 'accountant',
  receipts: 'receipts',
  t2: 'tax',
  'gst-pst': 'gst_pst',
  assets: 'assets',
  agm: 'agm',
  'corporate-records': 'corporate_records',
  'investor-readiness': 'investor_readiness',
  reconciliation: 'reconciliation',
  // Legacy aliases — kept so bookmarks / links from before the merge still resolve.
  'corporate-secretary': 'corporate_records',
  compliance: 'corporate_records',
};

export const AGENT_ID_TO_SLUG = {
  help: 'help',
  accountant: 'accountant',
  receipts: 'receipts',
  tax: 't2',
  gst_pst: 'gst-pst',
  assets: 'assets',
  agm: 'agm',
  corporate_records: 'corporate-records',
  investor_readiness: 'investor-readiness',
  reconciliation: 'reconciliation',
};

export function agentIdFromSlug(slug) {
  if (!slug || slug === 'command') return null;
  if (Object.prototype.hasOwnProperty.call(AGENT_SLUG_TO_ID, slug)) {
    return AGENT_SLUG_TO_ID[slug];
  }
  return null;
}

export function slugFromAgentId(agentId) {
  if (agentId == null || agentId === '') return 'command';
  return AGENT_ID_TO_SLUG[agentId] || 'command';
}

export function auditorPath(tab = 'business', agent = 'command') {
  const t = tab || 'business';
  if (t !== 'business') return `/opo/auditor/${t}`;
  const a = agent || 'command';
  if (a === 'command') return '/opo/auditor/business/command';
  return `/opo/auditor/business/${a}`;
}

/** Nav groups for the slim command list (Business tab). */
export const COMMAND_NAV_GROUPS = [
  {
    label: 'Overview',
    items: [{ slug: 'command', label: 'Command Center', meta: 'Compliance · tasks · intake' }],
  },
  {
    label: 'Ask',
    items: [{ slug: 'help', label: 'Help', meta: 'Grounded Q&A over Auditor records', featured: true }],
  },
  {
    label: 'Governance',
    items: [
      { slug: 'corporate-records', label: 'Corporate Records', meta: 'Minute book · resolutions · schedules', featured: true },
      { slug: 'agm', label: 'AGM', meta: 'First AGM · minute book' },
      { slug: 'investor-readiness', label: 'Investor Readiness', meta: 'Cap table · data room' },
    ],
  },
  {
    label: 'Corporate tax return',
    items: [{ slug: 't2', label: 'OptiPrime T2', meta: 'T2 · SR&ED · CRA assessment', featured: true }],
  },
  {
    label: 'Sales tax (GST & PST)',
    items: [{ slug: 'gst-pst', label: 'GST & PST', meta: 'GST34 · deadlines · PST', featured: true }],
  },
  {
    label: 'Finance & records',
    items: [
      { slug: 'accountant', label: 'Accountant', meta: 'ASPE books · bank statements · vendors' },
      { slug: 'receipts', label: 'Receipts & vendors', meta: 'Vendor invoices · SR&ED evidence' },
      { slug: 'assets', label: 'Assets & CCA', meta: 'Fixed assets · DGX Spark · depreciation' },
    ],
  },
  {
    label: 'Internal',
    items: [{ slug: 'reconciliation', label: 'Reconciliation', meta: 'Cross-checks' }],
  },
];
